import { apiClient } from '@/lib/api-client';

/** Uma oferta da `sales_offers`, como a busca do admin a devolve. */
export interface OfferOption {
  id: string;
  providerAccountId: string;
  providerAccountName: string | null;
  externalId: string;
  name: string;
  productName: string | null;
}

/** O que o Guru diz da oferta, antes de ela ser cadastrada. */
export interface GatewayOfferSummary {
  externalOfferId: string;
  name: string;
  /** Na unidade da moeda (`97` = R$ 97,00), como o Guru reporta — não em centavos. */
  value: number | null;
  currency: string | null;
  isActive: boolean;
  cycles: number | null;
  interval: number | null;
  intervalType: string | null;
}

export interface GatewayOfferLookup {
  /** Já está na `sales_offers`: a tela não oferece cadastrar de novo. */
  registeredOffer: OfferOption | null;
  summary: GatewayOfferSummary | null;
}

/**
 * Só o que o combo do cadastro precisa. Buscado aqui, e não pelo hook do
 * módulo `sales`, para `shared/` não depender de módulo.
 */
export interface OfferProviderAccount {
  id: string;
  provider: string;
  displayName: string;
}

export interface GatewayOfferIds {
  providerAccountId: string;
  externalProductId: string;
  externalOfferId: string;
}

/**
 * Ofertas do Sales (`/admin/sales/offers`). Vive em `shared/` porque é dado do
 * `sales` que outras telas escolhem — hoje a campanha de garantia.
 */
class OffersApiDatasource {
  /**
   * Busca exata: o id pode ser da oferta ou do produto. Uma página só, mas
   * larga: um id de produto devolve todas as ofertas dele, e no `perPage`
   * default (10) as demais sumiriam do campo sem aviso.
   */
  async findByExternalId(externalId: string): Promise<OfferOption[]> {
    const { data } = await apiClient.get<
      OfferOption[] | { items: OfferOption[] }
    >('/admin/sales/offers', { params: { externalId, perPage: 100 } });
    // ponytail: aceita o array de antes da rota paginar, para a busca não ficar
    // vazia enquanto o monolith e o admin-dashboard sobem em pipelines
    // separados. Sai quando os dois estiverem no ar com a rota paginada.
    return Array.isArray(data) ? data : data.items;
  }

  async listProviderAccounts(): Promise<OfferProviderAccount[]> {
    const { data } = await apiClient.get<OfferProviderAccount[]>(
      '/admin/sales/provider-accounts',
    );
    return data;
  }

  /** Confere no provedor, sem gravar. Pode demorar até um minuto. */
  async lookup(ids: GatewayOfferIds): Promise<GatewayOfferLookup> {
    const { data } = await apiClient.post<GatewayOfferLookup>(
      '/admin/sales/offers/lookup',
      ids,
    );
    return data;
  }

  async register(ids: GatewayOfferIds): Promise<OfferOption> {
    const { data } = await apiClient.post<OfferOption>(
      '/admin/sales/offers',
      ids,
    );
    return data;
  }
}

export const offersApiDatasource = new OffersApiDatasource();
